import React, { useEffect, useState, useCallback } from "react"
import { useApiClient } from "../services/api"
import { Edit, Trash2, DollarSign, Users, Target, FileText } from "lucide-react"
import { Card, CardContent } from "./ui/card"
import { Badge } from "./ui/badge"
import EditBountyModal from "./EditBountyModal"
import BountySubmissionsModal from "./BountySubmissionsModal"
import CustomLoader from "./CustomLoader"

interface EventBountiesProps {
    eventId: string
    lastUpdated?: number
}

interface Bounty {
    id: string
    title: string
    description: string
    value: number
    currency: string
    status: string
    type: string
    difficulty: string
    requirements: string[]
    tags: string[]
    maxWinners: number
    currentWinners: number
    startDate?: Date | string
    endDate?: Date | string
    participants: string[]
    createdAt: Date | string
    updatedAt: Date | string
    sponsorName?: string
    sponsorWebsite?: string
    sponsorLogo?: string
    swag?: string
}

export const EventBounties: React.FC<EventBountiesProps> = ({ eventId, lastUpdated }) => {
    const [bounties, setBounties] = useState<Bounty[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [editingBounty, setEditingBounty] = useState<Bounty | null>(null)
    const [isSubmissionsModalOpen, setIsSubmissionsModalOpen] = useState(false)
    const [selectedBountyForSubmissions, setSelectedBountyForSubmissions] = useState<Bounty | null>(null)
    const apiClient = useApiClient()

    const fetchBounties = useCallback(async () => {
        try {
            setLoading(true)
            const response = await apiClient.getHackdayBounties(eventId)

            if (response.success && response.data) {
                const bountiesWithDates = response.data.map((bounty: any) => ({
                    ...bounty,
                    startDate: bounty.startDate ? new Date(bounty.startDate) : undefined,
                    endDate: bounty.endDate ? new Date(bounty.endDate) : undefined,
                    createdAt: new Date(bounty.createdAt),
                    updatedAt: new Date(bounty.updatedAt),
                    // Map backend snake_case to frontend camelCase
                    sponsorName: bounty.sponsor_name || bounty.sponsorName,
                    sponsorWebsite: bounty.sponsor_website_url || bounty.sponsorWebsite,
                    sponsorLogo: bounty.sponsor_logo_url || bounty.sponsorLogo,
                    swag: bounty.swag,
                }))
                setBounties(bountiesWithDates)
                setError(null)
            } else {
                setError(response.error || "Failed to fetch event bounties")
            }
        } catch (err) {
            setError("An error occurred while fetching bounties")
            console.error("Error fetching event bounties:", err)
        } finally {
            setLoading(false)
        }
    }, [eventId, apiClient])

    useEffect(() => {
        fetchBounties()
    }, [fetchBounties, lastUpdated])

    const handleDeleteBounty = async (bountyId: string) => {
        if (!window.confirm("Are you sure you want to delete this bounty? This action cannot be undone.")) {
            return
        }

        try {
            const response = await apiClient.deleteBounty(bountyId)
            if (response.success) {
                setBounties((prev) => prev.filter((b) => b.id !== bountyId))
            } else {
                alert(response.message || "Failed to delete bounty")
            }
        } catch (err) {
            console.error("Error deleting bounty:", err)
            alert("Failed to delete bounty")
        }
    }

    const handleEditClick = (bounty: Bounty) => {
        setEditingBounty(bounty)
        setIsEditModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsEditModalOpen(false)
        setEditingBounty(null)
    }

    const handleSaveBounty = async (updatedData: any) => {
        if (!editingBounty) return

        try {
            const response = await apiClient.updateBounty(editingBounty.id, updatedData)

            if (response.success) {
                apiClient.invalidateCache("current-events-bounties")
                fetchBounties()
                handleCloseModal()
            } else {
                console.error("Failed to update bounty:", response.error)
                alert("Failed to update bounty: " + (response.error || "Unknown error"))
            }
        } catch (error) {
            console.error("Error updating bounty:", error)
            alert("An error occurred while updating the bounty")
        }
    }

    const handleViewSubmissions = (bounty: Bounty) => {
        setSelectedBountyForSubmissions(bounty)
        setIsSubmissionsModalOpen(true)
    }

    const handleCloseSubmissionsModal = () => {
        setIsSubmissionsModalOpen(false)
        setSelectedBountyForSubmissions(null)
    }

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "draft": return "bg-gray-100 text-gray-800 border-gray-300"
            case "active": return "bg-green-100 text-green-800 border-green-300"
            case "completed": return "bg-blue-100 text-blue-800 border-blue-300"
            case "cancelled": return "bg-red-100 text-red-800 border-red-300"
            default: return "bg-gray-100 text-gray-800 border-gray-300"
        }
    }

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty.toLowerCase()) {
            case "beginner": return "bg-green-100 text-green-800 border-green-300"
            case "intermediate": return "bg-yellow-100 text-yellow-800 border-yellow-300"
            case "advanced": return "bg-orange-100 text-orange-800 border-orange-300"
            case "expert": return "bg-red-100 text-red-800 border-red-300"
            default: return "bg-gray-100 text-gray-800 border-gray-300"
        }
    }

    if (loading) {
        return <div className="py-4 text-center text-gray-500">Loading bounties...</div>
    }

    if (error) {
        return <div className="py-4 text-center text-red-500">{error}</div>
    }

    if (bounties.length === 0) {
        return (
            <div className="py-8 text-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <Target className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-gray-500">No bounties created for this event yet.</p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                {bounties.map((bounty) => (
                    <div
                        key={bounty.id}
                        className="group flex items-center justify-between p-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                    >
                        {/* Left: Title & Status */}
                        <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center gap-3 mb-1">
                                <h4 className="text-sm font-semibold text-gray-900 truncate">{bounty.title}</h4>
                                <Badge className={`${getStatusColor(bounty.status || 'draft')} border text-[10px] px-1.5 py-0 font-medium`}>
                                    {bounty.status || 'Draft'}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                    <DollarSign className="h-3 w-3 text-green-600" />
                                    <span className="font-medium text-gray-700">
                                        {(bounty.value || 0).toLocaleString()} {bounty.currency || 'USD'}
                                    </span>
                                </span>
                                <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3 text-blue-600" />
                                    <span>{bounty.currentWinners || 0}/{bounty.maxWinners || 1} Winners</span>
                                </span>
                                <span className="capitalize">{bounty.difficulty || 'Beginner'}</span>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={() => handleViewSubmissions(bounty)}
                                className="p-1.5 hover:bg-blue-50 rounded-md transition-colors text-gray-500 hover:text-blue-600"
                                title="View submissions"
                            >
                                <FileText className="h-3.5 w-3.5" />
                            </button>
                            <button
                                onClick={() => handleEditClick(bounty)}
                                className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500 hover:text-gray-900"
                                title="Edit bounty"
                            >
                                <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                                onClick={() => handleDeleteBounty(bounty.id)}
                                className="p-1.5 hover:bg-red-50 rounded-md transition-colors text-gray-400 hover:text-red-600"
                                title="Delete bounty"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {isEditModalOpen && editingBounty && (
                <EditBountyModal
                    bounty={editingBounty}
                    onClose={handleCloseModal}
                    onSave={handleSaveBounty}
                />
            )}

            {isSubmissionsModalOpen && selectedBountyForSubmissions && (
                <BountySubmissionsModal
                    open={isSubmissionsModalOpen}
                    onClose={handleCloseSubmissionsModal}
                    bountyId={selectedBountyForSubmissions.id}
                    bountyTitle={selectedBountyForSubmissions.title}
                    bountyValue={selectedBountyForSubmissions.value}
                    bountyCurrency={selectedBountyForSubmissions.currency}
                />
            )}
        </div>
    )
}
